import React from 'react';

import { FormContainer, withValidationV2 } from '../common/Form.jsx';
import FormField from '../common/FormField.jsx';
import { default as _Image } from '../common/Image.jsx';
import { default as InventoryHelper } from '../common/Inventory.jsx';
import Layout from '../common/Layout.jsx';
import Name from '../common/Name.jsx';
import PageType from '../common/PageType.jsx';
import { ContentContainer } from '../common/Popup.jsx';
import Token from '../common/Token.jsx';
import useScrollLock from '../common/useScrollLock.jsx';

import Defaults from '../layout/Defaults.jsx';
import Kit from '../layout/Kit.jsx';

function Component(props) {
    useScrollLock(!props.builder || props.preview);
    React.useEffect(() => {
        if (props.builder) {
            return;
        }
        Logging.modal(props.inventory)
    }, []);
    let contentConfig = props.contentConfig;
    if (!contentConfig) {
        if (props.isPresentation) {
            contentConfig = props.config && props.config.documents && props.config.documents[Kit.Type.Presentation] && props.config.documents[Kit.Type.Presentation][PageType.Popup.Offer];
        } else {
            contentConfig = props.config && props.config.documents && props.config.documents[PageType.Popup.Offer];
        }
    }
    // Cache once — regenerating mints new block ids (React keys) and remounts the form, wiping input.
    const defaultConfig = React.useRef();
    if (!contentConfig) {
        if (!defaultConfig.current) {
            defaultConfig.current = Defaults.PageConfig[props.isPresentation ? PageType.Content.Presentation.Popup.Offer : PageType.Popup.Offer](props.config);
        }
        contentConfig = defaultConfig.current;
    }
    const handleBlockProps = (d) => {
        if (!d.type) {
            return;
        }
        let blockProps = {};
        switch (d.type.value) {
            case Name.Block.OfferForm.Container:
                blockProps = { editor: false, inventory: props.inventory, isPresentation: props.isPresentation, onLogSubscription: Logging.lead };
                break;
            case Name.Block.Inventory.Cell.Image:
                let containerProps;
                if (props.onPageProps) {
                    containerProps = props.onPageProps();
                } else if (contentConfig.desktop) {
                    containerProps = contentConfig.desktop.page;
                }
                blockProps = { backgroundColor: Layout.backgroundColorV2(props, containerProps) };
            // Intentional fallthrough
            case Name.Block.Inventory.Cell.Text:
                if (props.inventory) {
                    blockProps.data = props.inventory;
                }
                break;
        }
        return blockProps;
    }
    return <ContentContainer {...props} blockPropsHandler={handleBlockProps} className="ol--offer" contentConfig={contentConfig} />
}

const Helper = {
    calculateOfferPercent: (originalPrice, offerPrice) => {
        if (isNaN(originalPrice) || isNaN(offerPrice)) {
            return '';
        }
        if (originalPrice == 0) {
            return 0;
        }
        return Math.ceil((originalPrice - offerPrice) * 100 / originalPrice)
    },

    calculateOfferPrice: (originalPrice, percent) => {
        if (isNaN(originalPrice)) {
            return '';
        }
        if (isNaN(percent)) {
            return originalPrice.toFixed(2);
        }
        return ((1 - (percent / 100)) * originalPrice).toFixed(2);
    },

    formatCurrency: (value, separator) => {
        const _value = value + '';
        return _value.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    },

    handleOfferChange: (name, value, edition, onChange) => {
        switch (name) {
            case FormField.PricePercent: {
                const offerPrice = Helper.calculateOfferPrice(edition._Price, parseInt(value, 10));
                onChange('rawPrice', offerPrice);
                onChange(FormField.PriceFixed, offerPrice);
                onChange(name, value);
                break;
            }
            case FormField.PriceFixed: {
                if (value == undefined || value.length == 0) {
                    // Handle empty value
                    onChange('rawPrice', 0);
                    onChange(FormField.PricePercent, '');
                    onChange(name, '');
                    return;
                }
                if (value == '.') {
                    // Handle starting with decimal point
                    onChange('rawPrice', 0);
                    onChange(FormField.PricePercent, '0');
                    onChange(name, '0.');
                    return;
                }
                let _value = value;
                if (value.charAt(value.length - 1) == '.') {
                    _value = value.substring(0, value.length - 1);
                    if (_value.indexOf('.') >= 0) {
                        // Prevent duplicate decimal points
                        return;
                    }
                }
                const cleanValue = parseFloat(_value);
                if (isNaN(cleanValue) || _value.length != `${cleanValue}`.length) {
                    // Prevent non-decimal characters
                    return;
                }
                onChange('rawPrice', cleanValue);
                onChange(FormField.PricePercent, `${Helper.calculateOfferPercent(edition._Price, cleanValue) || 0}`);
                onChange(name, value);
                break;
            }
        }
    },

    initOfferState: (edition, onChange) => {
        const defaultOfferPercent = 5;
        onChange(FormField.PricePercent, defaultOfferPercent);

        const offerPrice = Helper.calculateOfferPrice(edition._Price, defaultOfferPercent);
        if (offerPrice) {
            onChange('rawPrice', offerPrice);
            onChange(FormField.PriceFixed, offerPrice);
        }
    }
};

const Logging = {
    lead: (_, state) => {
        if (!window || !window.gtag) {
            return;
        }
        gtag('event', 'generate_lead', { 'currency': 'USD', 'value': state.rawPrice })
    },

    modal: (inventory) => {
        if (inventory && window && window.gtag) {
            gtag('event', 'offer-modal', {
                'title': inventory.title
            });
        }
    }
};

function _OfferFormComponent(props) {
    const inventory = props.inventory || {};
    const edition = InventoryHelper.edition(inventory) || {};
    const localization = props.config && props.config.localization;
    React.useEffect(() => {
        if (props.builder) {
            return;
        }
        Helper.initOfferState(edition, props.onChange)
    }, []);
    React.useEffect(() => {
        if (!props.formData.token) {
            return;
        }
        const postData = {
            Email: props.formData.Email,
            Id: props.inventory.id,
            Name: props.formData.Name,
            Phone: props.formData.Phone,
            Price: props.formData.rawPrice,
            Robots: props.formData.token,
            Sub: props.config && props.config.subdomain
        }
        if (window && window.cookie) {
            postData.Referrer = cookie.getCookie(cookie._referrer);
            postData.Utm = cookie.getCookie(cookie._utm);
        }
        props.onSubmit('/api/offers', postData);
    }, [props.formData.token])
    const getSymbolProps = (blockConfig, display) => {
        let design, style;
        if (blockConfig && blockConfig.block) {
            design = blockConfig.block.designSymbol || Defaults.inputText();
            design = design.replace(`{${Token.InputText}}`, display);
            if (blockConfig.block.symbolLeft && blockConfig.block.symbolLeft.value && blockConfig.block.symbolLeft.value.length > 0) {
                style = { left: `${blockConfig.block.symbolLeft.value}${blockConfig.block.symbolLeft.unit}` };
            } else if (blockConfig.block.symbolRight && blockConfig.block.symbolRight.value && blockConfig.block.symbolRight.value.length > 0) {
                style = { right: `${blockConfig.block.symbolRight.value}${blockConfig.block.symbolRight.unit}` };
            }
        }
        return { design, style };
    }
    const handleBlockProps = (d) => {
        if (!d.type) {
            return;
        }
        switch (d.type.value) {
            case Name.Block.OfferForm.SubmitButtonV2:
                return { isPresentation: props.isPresentation };
        }
    }
    const handleChange = (name, value) => Helper.handleOfferChange(name, value, edition, props.onChange)
    const handleFormFieldProps = React.useCallback(({ blockConfig, type }, formFieldProps) => {
        switch (type) {
            case FormField.Email:
                formFieldProps.placeholderText = 'Email Address *';
                formFieldProps.type = 'email';
                formFieldProps.onSubmit = props.onValidate;
                break;
            case FormField[Name.Block.OfferForm.Container].ErrorMessage:
                formFieldProps.editor = props.editor;
                formFieldProps.error = props.formData.errorMessage;
                break;
            case FormField.Name:
                formFieldProps.placeholderText = 'Full Name *';
                formFieldProps.onSubmit = props.onValidate;
                break;
            case FormField.Phone:
                formFieldProps.placeholderText = 'Phone Number *';
                formFieldProps.onSubmit = props.onValidate;
                break;
            case FormField.PriceFixed: {
                const symbolProps = getSymbolProps(blockConfig, localization && localization.currencySymbol);
                formFieldProps.extras = <div className="symbol" dangerouslySetInnerHTML={{ __html: symbolProps.design }} style={symbolProps.style} />
                formFieldProps.placeholderText = 'Price (Fixed)';
                formFieldProps.wrapperClassName = 'input--with__symbol';
                formFieldProps.onChange = handleChange;
                formFieldProps.onSubmit = props.onValidate;
                break;
            }
            case FormField.PricePercent: {
                const symbolProps = getSymbolProps(blockConfig, '%');
                formFieldProps.extras = <div className="symbol" dangerouslySetInnerHTML={{ __html: symbolProps.design }} style={symbolProps.style} />
                formFieldProps.pattern = '[0-9]'
                formFieldProps.placeholderText = 'Price (Percent)';
                formFieldProps.type = 'number';
                formFieldProps.wrapperClassName = 'input--with__symbol';
                formFieldProps.onChange = handleChange;
                formFieldProps.onSubmit = props.onValidate;
                break;
            }
            case FormField[Name.Block.OfferForm.Container].SubmitButtonV1:
            case FormField[Name.Block.OfferForm.Container].SubmitButtonV2:
                formFieldProps.formState = props.formData.formState;
                formFieldProps.inPopup = props.inPopup;
                formFieldProps.isPresentation = props.isPresentation;
                formFieldProps.onClick = props.onValidate;
                break;
            case FormField[Name.Block.OfferForm.Container].SuccessMessage:
                formFieldProps.editor = props.editor;
                formFieldProps.formState = props.formData.formState;
                break;
        }
    }, [props.formData.errorMessage, props.formData.formState]);

    let contentConfig = props.contentConfig;
    if (!contentConfig) {
        if (props.blockConfig && props.blockConfig.layout && props.blockConfig.layout.design && props.blockConfig.layout.design.desktop && props.blockConfig.layout.design.desktop.blocks && props.blockConfig.layout.design.desktop.blocks.length > 0) {
            contentConfig = props.blockConfig.layout.design;
        } else {
            contentConfig = Defaults.Block[Name.Block.OfferForm.Container](props.config);
        }
    }
    return (
        <FormContainer.Component
            {...props}
            blockPropsHandler={handleBlockProps}
            builder={props.builder || props.disabled}
            contentConfig={contentConfig}
            inPopup={true}
            onFormFieldProps={handleFormFieldProps} />
    );
}
const OfferFormComponent = withValidationV2(_OfferFormComponent, [FormField.PriceFixed, FormField.PricePercent, 'Email', 'Name', 'Phone']);
const OfferForm = { Component: OfferFormComponent };

export {
    Component,
    OfferForm
}